/**
 * Responsibility: Manage application themes and dynamic world-based styling.
 * Scope: Singleton manager; interacts with gameStore and DOM.
 */
import { dispatcher } from './app-states'
import { TOPICS } from "@anyquest/shared-core"
import * as gameStore from './game-store'
import { deriveWorldPresentation, stringifyWorldPresentationStyle } from './engine/world-presentation'

class ThemeManager {
  private initialized = false

  public init() {
    if (this.initialized) return
    this.initialized = true

    dispatcher.on(TOPICS.game.loaded, (_e, gs) => this.updateWorldTheme(gs))
    dispatcher.on(TOPICS.game.started, (_e, gs) => this.updateWorldTheme(gs))
    dispatcher.on(TOPICS.game.roomChanged, (_e, room) => this.updateWorldTheme(gameStore.state, room))
  }

  private updateWorldTheme(gs: any, roomOverride: any = null) {
    const seed = gs?.world?.seed
    if (!seed) {
      this.applyTheme('', '')
      return
    }

    const roomText = roomOverride?.desc || roomOverride?.name || gs?.currentRoom?.desc || gs?.currentRoom?.name
    const pres = deriveWorldPresentation(seed, roomText || '', gs?.world?.taxonomy)
    
    this.applyTheme(`wt-${pres.variantName}`, stringifyWorldPresentationStyle(pres.style))
  }

  private applyTheme(className: string, inlineStyle: string) {
    if (typeof document === 'undefined') return

    const body = document.body
    const classes = Array.from(body.classList).filter(c => c.startsWith('wt-'))
    classes.forEach(c => body.classList.remove(c))

    if (className) body.classList.add(className)
    
    let styleTag = document.getElementById('world-theme-style') as HTMLStyleElement
    if (!styleTag) {
      styleTag = document.createElement('style')
      styleTag.id = 'world-theme-style'
      document.head.appendChild(styleTag)
    }
    
    styleTag.innerHTML = `:root { ${inlineStyle} }`
  }
}

export const themeManager = new ThemeManager()
